/**
 * Types et interfaces pour le système de sessions partagées VisualGrid
 */

// Types de base
export type SessionType = 'cloud' | 'local';
export type DeviceRole = 'server' | 'remote';
export type DeviceType = 'mobile' | 'desktop' | 'tablet';
export type SessionStatus = 'active' | 'inactive' | 'expired' | 'connecting';

// Informations d'un appareil dans une session
export interface SessionDevice {
  id: string;
  name: string;
  type: DeviceType;
  userAgent: string;
  role: DeviceRole;
  joinedAt: Date;
  lastSeen: Date;
}

// Informations de base d'une session
export interface SessionInfo {
  id: string;
  code: string; // Code court pour l'utilisateur (ex: ABC123)
  type: SessionType;
  status: SessionStatus;
  deviceId?: string; // ID de l'appareil actuel
  role?: DeviceRole;
  joinUrl?: string;
  qrCodeData?: string;
  devices?: SessionDevice[];
  config?: any; // Configuration VisualGrid actuelle
  createdAt?: Date;
  expiresAt?: Date;
}

// Capacités de l'appareil pour les sessions
export interface SessionCapabilities {
  hasInternet: boolean;
  canUseLocal: boolean;
  canUseWebRTC: boolean;
  localDevices: LocalDevice[];
  loading: boolean;
  error?: string;
}

// Appareil local détecté (pour sessions P2P)
export interface LocalDevice {
  id: string;
  name: string;
  ip: string;
  port: number;
  type: DeviceType;
  lastSeen: Date;
}

// Messages de synchronisation entre appareils
export interface SyncMessage {
  type: string;
  data: any;
  timestamp: number;
  sessionId: string;
  fromDeviceId: string;
}

// Types de messages de sync spécifiques à VisualGrid
export type VisualGridSyncMessageType = 
  | 'CONFIG_UPDATE'
  | 'ANIMATION_STATE_UPDATE'
  | 'TEXT_ELEMENTS_UPDATE'
  | 'LOGO_ELEMENTS_UPDATE'
  | 'SNAKE_STATE_UPDATE'
  | 'ACTIVE_PATTERN_TYPE_UPDATE'
  | 'SELF_TEST_UPDATE'
  | 'DEVICE_JOIN'
  | 'DEVICE_LEAVE'
  | 'SESSION_TERMINATED'
  | 'HEARTBEAT';

// Configuration spécifique pour créer une session
export interface CreateSessionConfig {
  type: SessionType;
  deviceInfo?: {
    name?: string;
    type?: DeviceType;
  };
  options?: {
    maxDevices?: number;
    expiresInHours?: number;
    requirePassword?: boolean;
    password?: string;
  };
}

// Configuration pour rejoindre une session
export interface JoinSessionConfig {
  sessionCode: string;
  deviceInfo?: {
    name?: string;
    type?: DeviceType;
  };
  password?: string;
}

// Callbacks pour les événements de session
export interface SessionEventCallbacks {
  onConfigUpdate?: (config: any) => void;
  onAnimationUpdate?: (animationData: any) => void;
  onAnimationStateUpdate?: (isAnimating: boolean) => void;
  onActivePatternTypeUpdate?: (activePatternType: string) => void;
  onDeviceJoin?: (device: SessionDevice) => void;
  onDeviceLeave?: (deviceId: string) => void;
  onSessionJoined?: (session: SessionInfo) => void;
  onSessionTerminated?: () => void;
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;
  onError?: (error: string) => void;
}

// Statistiques d'une session
export interface SessionStats {
  totalMessages: number;
  averageLatency: number;
  dataTransferred: number;
  uptime: number;
  lastSyncTime?: Date;
}

// Erreurs spécifiques aux sessions
export class SessionError extends Error {
  constructor(
    message: string,
    public code: 'NETWORK_ERROR' | 'SESSION_NOT_FOUND' | 'PERMISSION_DENIED' | 'SESSION_FULL' | 'SESSION_EXPIRED' | 'INVALID_CODE' | 'PRO_REQUIRED',
    public details?: any
  ) {
    super(message);
    this.name = 'SessionError';
  }
}

// Configuration pour le découverte d'appareils locaux
export interface LocalDiscoveryConfig {
  port: number;
  timeout: number;
  broadcastInterval: number;
  serviceName: string;
}

// Résultat de la découverte d'appareils locaux
export interface LocalDiscoveryResult {
  devices: LocalDevice[];
  success: boolean;
  error?: string;
}


